'use client'

import * as TabsPrimitive from '@radix-ui/react-tabs'
import * as React from 'react'
import Link from 'next/link'
import { cn } from '@scalis/utils'
import { Body } from '../typography'
import { BodySize, BodyType } from '../typography/components/body'
import {
  inverseBorderStyles,
  tabsHoverStyles,
  tabsListStyles,
  tabsRootStyles,
  tabsStyles,
  tabsTriggerStyles,
} from './filter-tabs.styles'
import type { TabProps, TabsContextValue } from './tabs.types'

export const TabsContext = React.createContext<TabsContextValue | undefined>(undefined)

export function useTabsContext() {
  const context = React.useContext(TabsContext)
  if (!context) {
    throw new Error('useTabsContext must be used within a TabsProvider')
  }
  return context
}

interface TabsProviderProps {
  children: React.ReactNode
  tabs: TabProps[]
  defaultValue?: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>['defaultValue']
  onValueChange?: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>['onValueChange']
  variant?: 'primary' | 'secondary' | 'tertiary'
  orientation?: 'horizontal' | 'vertical'
}

export function TabsProvider({
  children,
  tabs,
  variant = 'primary',
  orientation = 'horizontal',
  defaultValue,
  onValueChange: onValueChangeCallback,
}: TabsProviderProps) {
  const [value, setValue] = React.useState(defaultValue)

  React.useEffect(() => {
    const initialValue = defaultValue ?? tabs?.find((tab) => tab.isActive)?.id ?? tabs?.[0]?.id
    setValue(initialValue)
  }, [tabs, defaultValue])

  const onValueChange = React.useCallback(
    (value: string) => {
      setValue(value)
      onValueChangeCallback?.(value)
    },
    [onValueChangeCallback],
  )
  const contextValue = React.useMemo(
    () => ({
      tabs,
      variant,
      orientation,
      value,
      onValueChange,
    }),
    [tabs, variant, orientation, value, onValueChange],
  )
  return <TabsContext.Provider value={contextValue}>{children}</TabsContext.Provider>
}

/**
 *  Following state control props are not passed to the TabsRoot - "apply in TabsProvider instead"
 *  - value
 *  - defaultValue
 *  - onValueChange
 */
const TabsRoot = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  Omit<React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>, 'value' | 'defaultValue' | 'onValueChange'>
>(({ className, ...props }, ref) => {
  const { orientation, value, onValueChange } = useTabsContext()
  return (
    <TabsPrimitive.Root
      ref={ref}
      {...props}
      className={cn(tabsRootStyles({ orientation }), className)}
      value={value}
      onValueChange={onValueChange}
    />
  )
})
TabsRoot.displayName = TabsPrimitive.Root.displayName

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, ref) => {
  const { variant, orientation } = useTabsContext()
  return (
    <TabsPrimitive.List
      className={cn(tabsListStyles({ variant, orientation }), className)}
      ref={ref}
      {...props}
    >
      {children}
    </TabsPrimitive.List>
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    inverseBorder?: boolean
  }
>(({ className, children, inverseBorder, ...props }, ref) => {
  const { variant, orientation, value } = useTabsContext()
  const { leftSide, rightSide } = inverseBorderStyles()
  const shouldDisplayInverseBorder = inverseBorder && value === props.value
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerStyles({ variant, orientation }), className)}
      {...props}
    >
      <div className={cn(shouldDisplayInverseBorder && leftSide())}>
        <span className={cn('relative z-10', shouldDisplayInverseBorder && rightSide())}>{children}</span>
        <span className={tabsHoverStyles()} />
      </div>
    </TabsPrimitive.Trigger>
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsTriggerContent = React.forwardRef<
  HTMLDivElement,
  {
    tab: TabProps
    className?: string
    style?: React.CSSProperties
    index?: number
    isLast?: boolean
  }
>(({ tab, className, style, index, isLast }, ref) => {
  const { variant, orientation } = useTabsContext()
  const showSecondaryIcon = variant === 'secondary' && tab.icon
  const content = (
    <div
      className={'text-center'}
      style={style}
      ref={ref}
    >
      {showSecondaryIcon && (
        <div className=' mb-1 inline-flex h-10 w-10 items-center justify-center rounded-xl p-2 text-icon-neutral-00 group-data-[state=active]:bg-brand-primary-dark-00 group-data-[state=inactive]:bg-gray-200'>
          <i className={cn(tab.icon, 'w-4 text-base font-normal group-data-[state=inactive]:text-neutral-disabled')} />
        </div>
      )}
      <div className='flex flex-row items-center'>
        <Body
          size={BodySize.small}
          type={orientation === 'vertical' ? BodyType.basic : BodyType.emphasys}
          className={cn(tabsStyles({ variant }), className)}
        >
          {tab.title}
        </Body>
        {variant === 'tertiary' && (
          <hr className='h-3 border-r border-r-neutral-10 group-data-[state=active]:border-r-transparent' />
        )}
      </div>
    </div>
  )
  return tab.href ? <Link href={tab.href}>{content}</Link> : <React.Fragment>{content}</React.Fragment>
})
TabsTriggerContent.displayName = 'TabsTriggerContent'

const TabsContentWrapper = ({ children, className }: { children?: React.ReactNode; className?: string }) => {
  const { tabs } = useTabsContext()

  if (children) {
    return React.Children.map(children, (child) => {
      if (!React.isValidElement(child) || child.type !== TabsContent) return null
      return tabs.some((tab) => tab.id === (child.props as any).value) ? React.cloneElement(child) : null
    })
  }
  const hasContent = tabs.some((tab) => tab.content)
  if (hasContent) {
    return (
      <div className='flex grow overflow-x-hidden'>
        {tabs.map((tab) => (
          <TabsContent
            className={cn('w-full', className)}
            key={`tab-content-${tab.id}`}
            value={tab.id}
          >
            {tab.content}
          </TabsContent>
        ))}
      </div>
    )
  }

  return null
}
TabsContentWrapper.displayName = 'TabsContentWrapper'

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={className}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { TabsRoot, TabsList, TabsTrigger, TabsTriggerContent, TabsContentWrapper, TabsContent }
